import { makeObservable, observable, runInAction, computed } from "mobx";
import { RootStore } from "./RootStore";
import { TransportService } from "../services/TransportService";
import DataStore from "./DataStore";
import {
  Treatment,
  TreatmentPostRequest,
  TreatmentPutRequest,
  TreatmentsGetResponseSchema,
} from "../models/Treatments";
import {
  getLatestBasedOnStartDate,
  sortByDateFunction,
} from "../utils/dateHelpers";
import { convertTreatmentGetResponseToTreatments } from "../utils/helpers";

class TreatmentStore extends DataStore {
  rootStore: RootStore;
  treatments: Treatment[] | undefined = undefined;

  constructor(transportService: TransportService, rootStore: RootStore) {
    super(transportService);
    this.rootStore = rootStore;

    makeObservable(this, {
      treatments: observable,
      latestTreatments: computed,
      success: observable,
      error: observable,
    });
  }

  get latestTreatments() {
    if (this.treatments) {
      return getLatestBasedOnStartDate(this.treatments) as Treatment[];
    }
    return [] as Treatment[];
  }

  setTreatments(treatments: Treatment[] | undefined) {
    runInAction(() => {
      this.treatments = treatments ?? [];
    });
  }

  saveTreatment = async (treatmentPostRequest: TreatmentPostRequest) => {
    const token = await this.rootStore.keycloakStore.getRefreshedToken();
    if (!token) {
      return;
    }
    try {
      this.clearError("saveTreatment");
      this.setLoading("saveTreatment");
      await this.transportService.postWithToken(
        treatmentPostRequest,
        "/treatments",
        token
      );
      this.setSuccess("saveTreatment");
      this.getTreatments();
    } catch (e) {
      console.error(e);
      this.setError("saveTreatment");
      return;
    } finally {
      this.clearLoading("saveTreatment");
    }
  };

  updateTreatment = async (
    treatmentId: number,
    treatmentPutRequest: TreatmentPutRequest
  ) => {
    const token = await this.rootStore.keycloakStore.getRefreshedToken();
    if (!token) {
      return;
    }
    try {
      this.clearError("updateTreatment");
      this.setLoading("updateTreatment");
      await this.transportService.putWithToken(
        treatmentPutRequest,
        `/treatments/${treatmentId}`,
        token
      );
      this.setSuccess("updateTreatment");
      this.getTreatments();
    } catch (e) {
      console.error(e);
      this.setError("updateTreatment");
      return;
    } finally {
      this.clearLoading("updateTreatment");
    }
  };

  getTreatments = async () => {
    const token = await this.rootStore.keycloakStore.getRefreshedToken();
    if (!token) {
      return;
    }
    try {
      this.clearError("getTreatments");
      this.setLoading("getTreatments");

      const res = await this.transportService.getWithToken(
        "/treatments",
        token
      );

      if (res.data.treatments) {
        // TODO convert TreatmentGetResponse -> Treatment
        const treatments = convertTreatmentGetResponseToTreatments(
          res.data.treatments
        ).sort(sortByDateFunction((t: Treatment) => t.startDate));
        const parsedTreatments = TreatmentsGetResponseSchema.parse(treatments);
        this.setTreatments(parsedTreatments);
      } else {
        console.log("no treatments");
      }
    } catch (e) {
      console.error(e);
      this.setError("getTreatments");
      return;
    } finally {
      this.clearLoading("getTreatments");
    }
  };
}

export default TreatmentStore;
